import {
  AvatarHead,
  getIsUsageProhibited,
  isAvatarBig,
} from '@amplify/avatar-builder';
import clsx from 'clsx';
import kebabCase from 'lodash.kebabcase';
import { FC, KeyboardEvent, MouseEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { Tooltip } from 'src/components/tooltip';
import { KEYBOARD } from 'src/constants/keyboard.constants';
import { AVATAR_BUILDER_PATH } from 'src/constants/routes.constants';
import { useAvatar } from 'src/hooks/use-avatar.hook';
import { useCommonXapiContextPayloadHook } from 'src/hooks/use-common-xapi-context-payload.hook';
import { useIsMiddleSchoolView } from 'src/hooks/use-is-middle-school-view';
import { usePrevious } from 'src/hooks/use-previous.hook';
import { XApiEventKeys } from 'src/interfaces/event.types';
import authService from 'src/services/api/auth.service';
import eventService from 'src/services/event.service';
import {
  useCurrentGradeSelector,
  useIsGradeDefinedSelector,
  useIsViewDefinedSelector,
  useSelectedClassSelector,
} from 'src/store/features';

import './avatar.scss';

interface AvatarClassNames {
  container?: string;
  assets?: string;
  tooltip?: string;
}

interface Props {
  avatarClassNames?: AvatarClassNames;
}

const Avatar: FC<Props> = ({ avatarClassNames }) => {
  const { t } = useTranslation();
  const defaultTooltipTitle = t('Edit your Avatar');
  const usageProhibitedTooltipTitle = t('Time’s up for today!');

  const [tooltipTitle, setTooltipTitle] = useState(defaultTooltipTitle);

  const currentGrade = useCurrentGradeSelector();
  const isGradeDefined = useIsGradeDefinedSelector();
  const isViewDefined = useIsViewDefinedSelector();

  const isBig = isAvatarBig(currentGrade);
  const isMiddleSchoolView = useIsMiddleSchoolView();
  const commonXapiContextPayload = useCommonXapiContextPayloadHook();

  const selectedClass = useSelectedClassSelector();
  const prevSelectedClass = usePrevious(selectedClass);

  const tooltipArrowDirection = 'up';

  const kc = authService.getKC();

  const { characterAssets, saveAvatar } = useAvatar(currentGrade);

  const showTeacherAvatarBuilder = authService.isTeacher();

  const showMiddleSchoolAvatarBuilder =
    authService.isStudent() && isMiddleSchoolView;

  const headContainerClassNames = clsx(
    'avatar__head-container',
    {
      'avatar__head-container--ms-student': showMiddleSchoolAvatarBuilder,
      'avatar__head-container--k-5': !isMiddleSchoolView,
    },
    avatarClassNames?.container,
  );

  const headAvatarClassNames = clsx(
    'avatar__head-assets',
    {
      'avatar__head-assets--big': isBig && !showMiddleSchoolAvatarBuilder,
      'avatar__head-assets--teacher': showTeacherAvatarBuilder,
      'avatar__head-assets--ms-student': showMiddleSchoolAvatarBuilder,
      'avatar__head-assets--k-5': !isMiddleSchoolView,
    },
    avatarClassNames?.assets,
  );

  const handleNavLinkClick = (e: MouseEvent) => {
    if (e.type === 'contextmenu') {
      e.preventDefault();
    }

    eventService.send(
      XApiEventKeys.openedAvatarBuilder,
      {
        result: {
          destinationUrl: `${window.location.origin}${AVATAR_BUILDER_PATH}`,
        },
      },
      {
        result: {
          isRightClick: e.type === 'contextmenu',
          ...commonXapiContextPayload,
        },
      },
    );

    const userBk = authService.getUserBk();

    const isUsageProhibited = getIsUsageProhibited(userBk);

    if (isUsageProhibited) {
      e.preventDefault();
      setTooltipTitle(usageProhibitedTooltipTitle);
    } else {
      setTooltipTitle(defaultTooltipTitle);
    }
  };

  const handleNavLinkKeydown = (e: KeyboardEvent) => {
    const userBk = authService.getUserBk();

    const isUsageProhibited = getIsUsageProhibited(userBk);

    if (isUsageProhibited && e.key === KEYBOARD.enter.code) {
      e.preventDefault();
    }
  };

  const handleHoverAndFocus = () => {
    const userBk = authService.getUserBk();

    const isUsageProhibited = getIsUsageProhibited(userBk);

    if (isUsageProhibited) {
      setTooltipTitle(usageProhibitedTooltipTitle);
    } else {
      setTooltipTitle(defaultTooltipTitle);
    }
  };

  // reset stored assets on class change
  useEffect(() => {
    if (prevSelectedClass && prevSelectedClass !== selectedClass) {
      // reset big avatar stored assets
      saveAvatar(null, true);
      // reset small avatar stored assets
      saveAvatar(null, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass]);

  if (!isGradeDefined || !isViewDefined) return null;

  return (
    <Tooltip
      title={tooltipTitle}
      classNames={{
        tooltip: clsx('avatar__tooltip', avatarClassNames?.tooltip, {
          'avatar__tooltip--k-5': !isMiddleSchoolView,
        }),
      }}
      onFocus={handleHoverAndFocus}
      arrowDirection={tooltipArrowDirection}
    >
      <NavLink
        to={AVATAR_BUILDER_PATH}
        className={clsx('avatar', {
          'avatar--middle-school': isMiddleSchoolView,
          'avatar--k-5': !isMiddleSchoolView,
        })}
        aria-label="Go to avatar builder"
        data-testid="avatar"
        onMouseEnter={handleHoverAndFocus}
        onClick={handleNavLinkClick}
        onContextMenu={handleNavLinkClick}
        onKeyDown={handleNavLinkKeydown}
      >
        <AvatarHead
          grade={currentGrade}
          assets={characterAssets}
          onAvatarLoad={saveAvatar}
          config={{
            kc,
            ampEvents: eventService.getEvents(),
            appName: 'student-home',
          }}
          isTeacher={showTeacherAvatarBuilder}
          isMsStudent={showMiddleSchoolAvatarBuilder}
          containerClassNames={headContainerClassNames}
          avatarClassNames={headAvatarClassNames}
          xAPIEventsContextExtensions={{
            result: {
              'application-version':
                kebabCase(import.meta.env.VITE_APP_VERSION) || '1-0-0',
              view: isMiddleSchoolView ? 'middle-school' : 'elementary-school',
              'student-grade': currentGrade,
            },
          }}
        />
      </NavLink>
    </Tooltip>
  );
};

export default Avatar;
